import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import DepartmentSelectMutiple from '@/components/Form/Shared/DepartmentSelectMutiple'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import SubHeaders from '@/components/Headers/SubHeaders'
import LoadingBackdrop from '@/components/Loading/LoadingBackdrop'
import Paper from '@/components/Paper'
import { useFormPersistence } from '@/hooks/useFormPersistence'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import useUploadFile from '@/hooks/useUploadFile'
import { Attachment } from '@/modules/Upload/types'
import { renderFileItem } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
    Checkbox,
    GridHookForm,
    RadioGroup,
    TextField,
    Typogaphy,
} from 'vnetwork-ui'
import { getDocumentById, updateDocument } from '../../services'
import { DocumentCreatePayload } from '../../types/payload'
import { DocumentLevel, TypeDocument } from '../../types/utils'
import Audit from '../shared/Audit'
import { updateDocumentSchema } from './yup'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const UpdateForm: React.FC = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { documentID } = useParams()

    // get documentByID
    const {
        data: document,
        isLoading,
        isPending,
        renderState,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.DOCUMENT, documentID],
            queryFn: async () => await getDocumentById(Number(documentID)),
        },
        {
            loadingComponent: <LoadingBackdrop />,
            errorDisplayMode: 'page',
        }
    )
    const [hasExpired, setHasExpired] = useState(!!document?.result?.end_day)
    const [releaseScope, setReleaseScope] = useState<
        'patch' | 'minor' | 'major'
    >('patch')

    // ✅ Memoize defaultValues properly
    const defaultValues = useMemo(
        () =>
            ({
                name_file: document?.result?.name_file ?? '',
                descriptions: document?.result?.descriptions ?? '',
                document_level: document?.result?.document_level ?? '',
                document_type: document?.result?.document_type ?? '',
                department_id: document?.result?.department_id ?? '',
                sharing_enable: !!document?.result?.sharing_enable,
                start_day: document?.result?.start_day
                    ? dayjs(document?.result?.start_day).toDate()
                    : undefined,
                end_day: document?.result?.end_day
                    ? dayjs(document?.result?.end_day).toDate()
                    : undefined,
                employee_id: document?.result?.employee_id ?? 0,
                departments: document?.result?.departments ?? [],
                employees: document?.result?.employees ?? [],
                file: document?.result?.versions?.[0]?.attachment
                    ? [document?.result?.versions?.[0]?.attachment]
                    : [],
                file_path: document?.result?.file_path ?? '',
                assignees: document?.result?.assignees ?? [],
                document_status: document?.result?.document_status ?? [],
            }) as unknown as DocumentCreatePayload,
        [document?.result]
    )

    const formControl = useForm<
        DocumentCreatePayload,
        any,
        DocumentCreatePayload
    >({
        defaultValues,
        //@ts-ignore
        resolver: yupResolver(updateDocumentSchema(t, hasExpired)),
    })

    // ✅ Optimize useEffect - chỉ depend vào document?.result
    const documentResult = document?.result
    useEffect(() => {
        if (documentResult && !isLoading && !isPending) {
            formControl.reset(defaultValues)
        }
    }, [documentResult, formControl.reset, isLoading, isPending, defaultValues])

    // ✅ Memoize renderFileItem callback
    const handleRenderFileItem = (file: File | Attachment) => {
        return (
            <div className="grid grid-cols-4 gap-2">
                <div className="col-span-1">{renderFileItem(file)}</div>
            </div>
        )
    }
    useFormPersistence(
        formControl,
        ['document', document?.result?.document_id]?.join('-'),
        defaultValues
    )

    const { uploadAsync } = useUploadFile({
        prefix: 'document',
        onSuccess: (attachment) => {
            if (attachment?.[0]?.status !== 'fulfilled') {
                return toast.error(t('upload-file-failed'))
            }
        },
    })
    const { refetch } = useQueryClientCallback({
        pattern: 'document',
        mode: 'contains',
    })

    const { mutateAsync: callUpdateDocument, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: updateDocument,
            },
            {
                reactQueryKeys: QUERY_KEYS.DOCUMENTS,
                successMessage: 'Update Document Successfully',
                errorMessage: 'something went wrong',
                onSuccess: () => {
                    refetch()
                    navigate(generatePathDatacentral(paths.documentList, {}), {
                        replace: true,
                    })
                },
            }
        )

    const handleCreateDocument = async (values: DocumentCreatePayload) => {
        let attachments: Attachment[] = []
        let documentFilePath =
            document?.result?.versions?.[0]?.attachment?.attachment_file_url ||
            ''
        const newFiles = values?.file?.filter((file) => file instanceof File)
        if (newFiles.length > 0) {
            // upload only when user upload new file
            const attachmentUploadeds = await uploadAsync(newFiles)

            attachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]
            documentFilePath = attachments[0].attachment_file_url || ''
        }

        await callUpdateDocument({
            documentID: document?.result?.document_id || 0,
            document: {
                ...values,
                file_path: documentFilePath,
                end_day: hasExpired ? undefined : values.end_day,
                sharing_enable: values.sharing_enable,
                departments: values.sharing_enable ? values.departments : [],
                employees: values.sharing_enable ? values.employees : [],
                attachments: attachments,
                release_scope: releaseScope,
            },
        })
    }

    // ✅ Memoize navigation handler
    const handleCancel = useCallback(() => {
        navigate(generatePathDatacentral(paths.documentList, {}))
    }, [navigate])

    // ✅ Memoize expired toggle handler
    const handleExpiredToggle = useCallback(() => {
        setHasExpired(!hasExpired)
    }, [hasExpired])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.documentList,
                        title: t('document_list'),
                    },
                    {
                        path: paths.documentUpdate,
                        title: document?.result?.name_file || '',
                    },
                    {
                        path: paths.documentUpdate.replace(':documentID', ''),
                        title: t('update_document'),
                    },
                ]}
            />
            <Paper className="mt-2 flex xs:flex-col md:flex-row-reverse">
                <div className="w-full">
                    <Typogaphy className="font-medium text-primary mb-4">
                        {t('update_document')}
                    </Typogaphy>
                    <GridHookForm
                        formControl={formControl}
                        data={[
                            {
                                formItemProps: {
                                    name: 'name_file',
                                    label: t('title'),
                                    type: 'textfield',
                                    size: 'small',
                                    placeholder: t('title'),
                                },
                                className: 'col-span-2',
                            },
                            {
                                className: 'col-span-2',
                                renderEditComponent: () => (
                                    <TextField
                                        size="small"
                                        label={t('document_id_response')}
                                        value={
                                            document?.result
                                                ?.document_id_reponse
                                        }
                                        disabled
                                    />
                                ),
                            },
                            {
                                formItemProps: {
                                    name: 'descriptions',
                                    label: t('description'),
                                    type: 'textarea',
                                    size: 'small',
                                    placeholder: t('description'),
                                },
                                className: 'col-span-4',
                            },
                            {
                                formItemProps: {
                                    name: 'document_type',
                                    label: t('document_type'),
                                    type: 'select',
                                    size: 'small',
                                    placeholder: t('document_type'),
                                    options: TypeDocument(t),
                                },
                                className: 'col-span-2',
                            },

                            {
                                formItemProps: {
                                    name: 'document_level',
                                    label: t('document_level'),
                                    type: 'select',
                                    size: 'small',
                                    placeholder: t('document_level'),
                                    options: DocumentLevel(t),
                                },
                                className: 'col-span-2',
                            },
                            {
                                className: 'col-span-2',
                                formItemProps: {
                                    name: 'sharing_enable',
                                    label: t('document.sharing.enable'),
                                    type: 'checkbox',
                                    size: 'small',
                                    placeholder: t('document.sharing.enable'),
                                },
                            },
                            {
                                className: 'col-span-2',
                                renderEditComponent: (control) => (
                                    <DepartmentSelect
                                        label="document.department.identity"
                                        control={control}
                                        name="department_id"
                                    />
                                ),
                            },
                            {
                                className: formControl.watch('sharing_enable')
                                    ? 'col-span-4'
                                    : 'hidden',
                                renderEditComponent: (control) => (
                                    <DepartmentSelectMutiple
                                        control={control}
                                        multiple
                                        name="departments"
                                    />
                                ),
                            },
                            {
                                className: formControl.watch('sharing_enable')
                                    ? 'col-span-4'
                                    : 'hidden',
                                renderEditComponent: (control) => (
                                    <EmployeeSelect
                                        showLabel
                                        label="select_people_to_share"
                                        name="employees"
                                        control={control}
                                        multiple
                                    />
                                ),
                            },
                            {
                                formItemProps: {
                                    name: 'start_day',
                                    label: t('start_day'),
                                    type: 'datepicker',
                                    size: 'small',
                                    placeholder: t('start_day'),
                                },
                                className: 'col-span-2',
                            },
                            {
                                className: 'col-span-1',
                                formItemProps: {
                                    disabled: !hasExpired,
                                    name: 'end_day',
                                    label: t('end_day'),
                                    type: 'datepicker',
                                    size: 'small',
                                    placeholder: t('end_day'),
                                },
                            },
                            {
                                className: 'col-span-1',
                                renderEditComponent: () => (
                                    <Checkbox
                                        label={t('document.expired')}
                                        checked={hasExpired}
                                        onChange={handleExpiredToggle}
                                    />
                                ),
                            },
                            {
                                renderEditComponent: (control) => (
                                    <EmployeeSelect
                                        showLabel
                                        name="assignees"
                                        control={control}
                                        multiple
                                    />
                                ),
                                className: 'col-span-4',
                            },
                            {
                                renderEditComponent: () => (
                                    <RadioGroup
                                        value={releaseScope}
                                        direction="row"
                                        onChange={(e) =>
                                            setReleaseScope(
                                                e as 'patch' | 'minor' | 'major'
                                            )
                                        }
                                        options={[
                                            {
                                                label: t('release-as-patch'),
                                                value: 'patch',
                                            },
                                            {
                                                label: t('release-as-minor'),
                                                value: 'minor',
                                            },
                                            {
                                                label: t('release-as-major'),
                                                value: 'major',
                                            },
                                        ]}
                                    />
                                ),
                                className: 'col-span-4',
                            },
                            {
                                formItemProps: {
                                    name: 'file',
                                    label: t('document'),
                                    type: 'files',
                                    size: 'small',
                                    placeholder: t('document'),
                                    renderFileItem: (
                                        file: File | Attachment
                                    ) => {
                                        return handleRenderFileItem(file)
                                    },
                                },
                                className: 'col-span-4',
                            },
                        ]}
                        actions={[
                            {
                                title: t('cancel-operation'),
                                type: 'button',
                                variant: 'outlined',
                                handler: handleCancel,
                                disabled: isUpdating,
                            },
                            {
                                title: t('release'),
                                type: 'submit',
                                disabled: isUpdating,
                                loading: isUpdating,
                                variant: 'contained',
                            },
                        ]}
                        onSubmit={handleCreateDocument}
                    />
                </div>
            </Paper>
            <Audit />
        </>
    )
}

export const DocumentUpdate = () => {
    return <UpdateForm />
}
